import { useState, useEffect, useRef } from "react";

/**
 * Animates a numeric string from 0 to target over 2s when trigger becomes true.
 * Handles suffixed values like "7+", "1.5M+", "10k+".
 * Non-numeric values (e.g. "PR") are returned unchanged immediately.
 */
export function useCountUp(target, trigger) {
  const [display, setDisplay] = useState(String(target));
  const rafRef = useRef(null);

  useEffect(() => {
    if (!trigger) return;

    const raw = String(target);
    const match = raw.match(/^([\d.]+)(.*)$/);

    if (!match) {
      setDisplay(raw);
      return;
    }

    const endVal = parseFloat(match[1]);
    const suffix = match[2];
    const isFloat = match[1].includes(".");
    const startTime = performance.now();
    const duration = 2000;

    const tick = (now) => {
      const progress = Math.min((now - startTime) / duration, 1);
      // Expo.out easing — matches UI Pro Max recommendation
      const eased = 1 - Math.pow(1 - progress, 3);
      const current = endVal * eased;
      const formatted = isFloat
        ? current.toFixed(1)
        : Math.round(current).toString();
      setDisplay(formatted + suffix);
      if (progress < 1) {
        rafRef.current = requestAnimationFrame(tick);
      }
    };

    rafRef.current = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(rafRef.current);
  }, [trigger, target]);

  return display;
}
